import { useContext } from "react";
import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { SessionContext } from "../common/contexts/SessionContext";
import { NotSet } from "../common/misc/UIconstants";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { DatasetsTile } from "../common/sidebar/tiles/DatasetsTile";
import { SamplesTile } from "../common/sidebar/tiles/SamplesTile";
import { PersonProjectPermissions } from "../ProjectPersonPermissions/PersonProjectPermissions";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { CustomTypeDetailRow, CustomTypeRenderer } from "../Customization/CustomTypes/generics/CustomTypeRenderer";
import { InventoriesTile } from "../common/sidebar/tiles/InventoriesTile";
import { CustomFieldReferencesTile } from "../common/sidebar/tiles/CustomFieldReferencesTile";
import { EntitySidebarTileAutosorter } from "../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityDeleteButton } from "../Entities/GenericComponents/common/GenericEntityDetailPage";
import { LabNotebooksTile } from "../ELN/common/tiles/LabNotebooksTile";
import { EntityEditButton } from "../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../common/entity/entityComponents/EntityCloneButton";
import { EntityComplianceDropdown } from "../common/entity/entityComponents/EntityComplianceDropdown";
import { GenericEntityExportButton } from "../Entities/GenericComponents/common/GenericEntityExportButton";

export interface ProjectDetailTableProps extends EntityDetailTableProps<"projects"> {
  hidePermissionSection?: boolean;
}

export const ProjectsDetailTable = ({
  entity,
  entityConstants,
  entityTypeId,
  fieldLabels,
  showLinks = true,
  hidePermissionSection = false,
}: ProjectDetailTableProps) => {
  const { isPrintContext } = useContext(SessionContext);
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>Project-ID: {entity.id}</span>
                </div>
              </div>
            }
          />

          <CustomTypeDetailRow entity={entity} entityTypeId={entityTypeId} showLinks={showLinks} />
        </Table.Body>
      </Table>
      <CustomTypeRenderer entity={entity} showLinks={false} />
      {!hidePermissionSection && !isPrintContext && (
        <Table>
          <Table.Head>{fieldLabels.projectPersonPermissions}</Table.Head>
          <Table.Body>
            <PersonProjectPermissions project={entity} />
          </Table.Body>
        </Table>
      )}
    </>
  );
};

export const ProjectsDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"projects">) => {
  const { session } = useContext(SessionContext);

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          controls={
            <>
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />
            </>
          }
          controlsDropdown={
            <>
              <li>
                <EntityCloneButton
                  entity={entity}
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                />
              </li>
              <li>
                <GenericEntityExportButton entity={entity} entityTypeId={entityTypeId} />
              </li>
              <EntityComplianceDropdown entity={entity} entityTypeId={entityTypeId} />
              <li className="divider" />
              <li>
                <EntityDeleteButton entity={entity} entityTypeId={entityTypeId} alwaysDeletePermanently />
              </li>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <ProjectsDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
        />
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          <DatasetsTile defaultFilters={{ projectIds: [entity.id] }} excludeFilters={{ projectIds: () => false }} />
          <SamplesTile defaultFilters={{ projectIds: [entity.id] }} excludeFilters={{ projectIds: () => false }} />
          <InventoriesTile defaultFilters={{ projectIds: [entity.id] }} excludeFilters={{ projectIds: () => false }} />
          {!!session?.features.eln && (
            <LabNotebooksTile
              defaultFilters={{ projectIds: [entity.id] }}
              excludeFilters={{ projectIds: () => false }}
              rowControls={() => null}
            />
          )}
          <CustomFieldReferencesTile
            entityType="Project"
            entity={entity}
            labNotebookFilters={session?.features.eln ? { projectIds: [entity.id] } : undefined}
            labNotebookEntryFilters={session?.features.eln ? { projectIds: [entity.id] } : undefined}
          />
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
